import { useCallback, useEffect, useRef, useState } from 'react';
import { getEventsUrl } from './core/client';

/**
 * SSE client for the orders server's `/api/events` (orchestration §3e). The
 * wire format is sundae's: one `id: <seq>` line plus a `data:` line carrying
 * the JSON event, no named event types, so the browser's default `message`
 * handler sees everything and `lastEventId` tracks the sequence for replay on
 * reconnect. Lifted from the mail app's `api/realtime.ts` + `useEventStream`,
 * with the mail event union replaced by the two events this app emits.
 */

export type RealtimeEvent =
  | { type: 'connected' }
  | { type: 'resync' }
  /** A journey changed — refetch the lists rather than patching in place. */
  | { type: 'orders.updated'; order_id?: string | null }
  /** Package statuses moved — same contract: a nudge to refetch. */
  | { type: 'packages.updated' };

const KNOWN_TYPES = new Set(['connected', 'resync', 'orders.updated', 'packages.updated']);

export function parseEventStreamData(data: string): RealtimeEvent | null {
  try {
    const parsed = JSON.parse(data);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    const type = String(parsed.type || '');
    if (!KNOWN_TYPES.has(type)) {
      // A type the client has no handler for is contract drift, not something
      // to silently swallow — say so once and drop it.
      console.warn('[realtime] Dropping unrecognized event type:', type);
      return null;
    }
    if (type === 'orders.updated') {
      return { type, order_id: parsed.order_id != null ? String(parsed.order_id) : null };
    }
    return { type } as RealtimeEvent;
  } catch (error) {
    console.warn('[realtime] Failed to parse event', error);
    return null;
  }
}

const BACKOFF_MS = [1000, 2000, 5000, 10000, 20000];
const MAX_RECONNECT_ATTEMPTS = 8;
const COOLDOWN_MS = 60_000;

export type EventStreamStatus = 'idle' | 'connecting' | 'connected' | 'error';

export function useEventStream(
  onEvent: (event: RealtimeEvent) => void,
  options: { enabled?: boolean; authToken?: string | null } = {}
): EventStreamStatus {
  const { enabled = true, authToken = null } = options;
  const callbackRef = useRef(onEvent);
  callbackRef.current = onEvent;

  const [status, setStatus] = useState<EventStreamStatus>('idle');
  const statusRef = useRef<EventStreamStatus>('idle');
  const lastEventIdRef = useRef<string | null>(null);

  const updateStatus = useCallback((next: EventStreamStatus) => {
    if (statusRef.current === next) return;
    statusRef.current = next;
    setStatus(next);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
      updateStatus('error');
      return undefined;
    }
    if (!enabled || !authToken) {
      updateStatus('idle');
      return undefined;
    }

    let cancelled = false;
    let reconnectAttempts = 0;
    let source: EventSource | null = null;
    let reconnectTimer: number | null = null;
    let cooldownTimer: number | null = null;

    const restart = () => {
      if (cancelled) return;
      source?.close();
      source = null;
      start();
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        if (cooldownTimer !== null) return;
        console.warn('[realtime] Max reconnect attempts reached. Cooling down…');
        cooldownTimer = window.setTimeout(() => {
          cooldownTimer = null;
          reconnectAttempts = 0;
          restart();
        }, COOLDOWN_MS);
        return;
      }
      const delay = BACKOFF_MS[Math.min(reconnectAttempts, BACKOFF_MS.length - 1)];
      reconnectAttempts += 1;
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        restart();
      }, delay + Math.random() * 500);
    };

    function start() {
      if (cancelled) return;
      updateStatus('connecting');
      const next = new EventSource(getEventsUrl(lastEventIdRef.current));
      source = next;

      next.onopen = () => {
        reconnectAttempts = 0;
        if (cooldownTimer !== null) {
          window.clearTimeout(cooldownTimer);
          cooldownTimer = null;
        }
        updateStatus('connected');
      };

      next.onmessage = (event) => {
        if (event.lastEventId) {
          lastEventIdRef.current = event.lastEventId;
        }
        if (!event.data || event.data.trim() === '') return;
        const parsed = parseEventStreamData(event.data);
        if (parsed) {
          callbackRef.current(parsed);
        }
      };

      next.onerror = () => {
        next.close();
        if (cancelled) return;
        updateStatus('error');
        scheduleReconnect();
      };
    }

    start();

    return () => {
      cancelled = true;
      source?.close();
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      if (cooldownTimer !== null) window.clearTimeout(cooldownTimer);
      updateStatus('idle');
    };
  }, [enabled, authToken, updateStatus]);

  return status;
}
